// Single source of truth for what a Stripe subscription status permits.
//
// Two different questions get asked about a subscription, and they are NOT the same:
//
//   1. "May this user consume paid content?"  -> ["active", "trialing"]
//   2. "May this user reach the billing portal to fix their card?"
//
// Conflating them is what caused T-051: a failed payment moves a subscription to
// `past_due`, which correctly revokes content access — but it was also revoking access
// to the billing portal, i.e. the one control that fixes a failed payment. The customer
// was locked out of the fix by the problem the fix addresses.
//
// This module owns question 2 only. Question 1 stays where it was, as `planIsActive`
// in `src/util/auth.js`, deliberately untouched.

// Stripe subscription statuses where opening the Customer Portal is useful.
//
// Includes the recoverable failure states (`past_due`, `unpaid`, `incomplete`) so the
// customer can replace a failing card, `paused` so they can resume, and `canceled` so
// they can still retrieve invoices and re-add a payment method.
//
// Excludes `incomplete_expired`, which is terminal — the subscription never activated
// and Stripe cannot revive it, so there is nothing to manage.
export const BILLING_PORTAL_STATUSES = [
  "active",
  "trialing",
  "past_due",
  "unpaid",
  "canceled",
  "incomplete",
  "paused",
];

/**
 * Whether a user in this subscription status should be able to reach the Stripe
 * billing portal.
 *
 * @param {string|null|undefined} stripeSubscriptionStatus - status from the user record
 * @returns {boolean} true if the billing portal should be reachable
 */
export function canManageBilling(stripeSubscriptionStatus) {
  return BILLING_PORTAL_STATUSES.includes(stripeSubscriptionStatus);
}
